import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const init = { name: "", phone: "", preferred_time: "", note: "" };

export default function CallbackWidget() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("callback");
  const [form, setForm] = useState(init);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success(t("widget.successToast"));
      setForm(init);
      setOpen(false);
    } catch {
      toast.error(t("widget.errorToast"));
    } finally {
      setLoading(false);
    }
  };

  const goContact = () => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      window.location.href = "/#contact";
      return;
    }
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <button
        data-testid="callback-trigger"
        onClick={() => setOpen((v) => !v)}
        className="fixed z-50 right-5 bottom-20 sm:right-7 sm:bottom-24 group"
        aria-label={t("widget.callMeBack")}
      >
        <span className="relative flex items-center gap-2.5 h-12 pl-3.5 pr-5 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] widget-shadow border border-white/10 hover:border-white/20 transition-colors">
          <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
            <i className={`fa-solid ${open ? "fa-xmark" : "fa-phone"} text-[12px]`} />
          </span>
          <span className="font-medium text-sm hidden sm:block">
            {open ? t("widget.close") : t("widget.callMeBack")}
          </span>
        </span>
      </button>

      <div
        data-testid="callback-panel"
        className={`fixed z-50 right-3 sm:right-7 bottom-40 sm:bottom-44 w-[calc(100%-1.5rem)] sm:w-[380px] origin-bottom-right transition-all duration-300 ${
          open ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-3 scale-95 pointer-events-none"
        }`}
      >
        <div className="rounded-2xl bg-[hsl(var(--bg-elev))] border border-[hsl(var(--line))] widget-shadow overflow-hidden">
          <div className="p-5 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] relative">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">{t("widget.talkToUs")}</div>
                <div className="mt-1 font-display text-lg font-semibold leading-tight">{t("widget.tagline")}</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="callback-close"
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                aria-label={t("widget.close")}
              >
                <i className="fa-solid fa-xmark text-xs" />
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-1 p-1 rounded-full bg-white/10">
              <button
                data-testid="tab-callback"
                onClick={() => setTab("callback")}
                className={`text-xs h-8 rounded-full font-medium transition-colors ${
                  tab === "callback" ? "bg-[hsl(var(--bg))] text-[hsl(var(--ink))]" : "text-white/85"
                }`}
              >
                <i className="fa-solid fa-phone mr-1.5" />
                {t("widget.tabCallback")}
              </button>
              <button
                data-testid="tab-contact"
                onClick={() => setTab("contact")}
                className={`text-xs h-8 rounded-full font-medium transition-colors ${
                  tab === "contact" ? "bg-[hsl(var(--bg))] text-[hsl(var(--ink))]" : "text-white/85"
                }`}
              >
                <i className="fa-solid fa-message mr-1.5" />
                {t("widget.tabContact")}
              </button>
            </div>
          </div>

          {tab === "callback" ? (
            <form onSubmit={submit} className="p-5 space-y-4" data-testid="callback-form">
              <Field label={t("widget.yourName")} name="name" value={form.name} onChange={onChange} required />
              <Field label={t("widget.phone")} name="phone" value={form.phone} onChange={onChange} required />
              <Field label={t("widget.bestTime")} name="preferred_time" value={form.preferred_time} onChange={onChange} placeholder={t("widget.bestTimePlaceholder")} />
              <button
                type="submit"
                disabled={loading}
                data-testid="callback-submit"
                className="w-full h-11 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] font-medium hover:bg-[hsl(var(--accent))] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {loading ? t("widget.sending") : t("widget.requestCallback")}
                <i className="fa-solid fa-arrow-right text-xs" />
              </button>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <a
                  href="https://wa.me/41793150600"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="widget-whatsapp"
                  className="h-10 rounded-full bg-[#25D366] text-white font-medium text-sm inline-flex items-center justify-center gap-2"
                >
                  <i className="fa-brands fa-whatsapp" />
                  WhatsApp
                </a>
                <a
                  href="tel:+41325100620"
                  data-testid="widget-call"
                  className="h-10 rounded-full border border-[hsl(var(--line))] hover:border-[hsl(var(--ink))] font-medium text-sm inline-flex items-center justify-center gap-2 transition-colors"
                >
                  <i className="fa-solid fa-phone" />
                  {t("widget.callNow")}
                </a>
              </div>
            </form>
          ) : (
            <div className="p-5">
              <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{t("widget.preferFullForm")}</p>
              <button
                onClick={goContact}
                data-testid="widget-contact-go"
                className="mt-4 w-full h-11 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] font-medium hover:bg-[hsl(var(--accent))] transition-colors inline-flex items-center justify-center gap-2"
              >
                {t("widget.openContactForm")}
                <i className="fa-solid fa-arrow-down text-xs" />
              </button>
              <div className="mt-5 pt-5 border-t border-[hsl(var(--line))] space-y-3 text-sm">
                <a href="mailto:hello@webprintmedia.swiss" className="flex items-center gap-3 hover:text-[hsl(var(--accent))]">
                  <i className="fa-solid fa-envelope text-[hsl(var(--accent))]" />
                  hello@webprintmedia.swiss
                </a>
                <a href="tel:+41325100620" className="flex items-center gap-3 hover:text-[hsl(var(--accent))]">
                  <i className="fa-solid fa-phone text-[hsl(var(--accent))]" />
                  +41 32 510 06 20
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Field({ label, name, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
        {label}{required && <span className="text-[hsl(var(--accent))]"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={`callback-input-${name}`}
        className="mt-1.5 w-full bg-transparent border-b border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none py-2 text-sm transition-colors"
      />
    </div>
  );
}
